import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './plans.scss';
import CheckCircleIcon from '../../assets/icon-components/CheckCircle';
import arrowRightIcon from '../../assets/icons/whiteArrow.png';
import useMediaQuery from '@mui/material/useMediaQuery';
import bannerImg1 from '../../assets/images/BannerCoreVitalsMembership.png';
import bannerImg2 from '../../assets/images/BannerCoreVitalsPlusMembership.png';
import bannerImg3 from '../../assets/images/BannerFullBodyMRI.png';
import checkIcon from '../../assets/icons/CheckTick.svg';
import AccordArrow from '../../assets/icon-components/AccordArrow';
import TextInput from '../../components/BaseComponents/TextInput';
import arrow from '../../assets/icons/Chevron-down.svg';
import PhoneInputComp from '../../components/BaseComponents/PhoneInput';
import { getStripeProducts, StripeProduct } from '../../api/subscription';
import { firePlanChange, firePaymentTypeChange } from '../../utils/dataLayer';
import {
  getPlanFallbackAmount,
  renderCheckoutPlanPrice,
} from '../../utils/rhPromoPricing';
import { useRhReferralPricing } from '../../utils/useRhReferralPricing';
const PlanHeader = ({
  title,
  description,
  price,
  id,
  selectedPlan,
  onSelect
}: {
  title: string;
  description: string;
  price: React.ReactNode;
  id: string;
  selectedPlan: string;
  onSelect: (id: string) => void;
}) => {
  return (
    <div className='plan-card-header d-flex' >
      <div>
        <input
          type='radio'
          name='plan-type'
          className='plan-type-radio'
          checked={selectedPlan === id}
          onChange={() => onSelect(id)}
        />
        <span className='radio-btn'></span>
      </div>
      <div className='plan-card-header-info' >
        <div className='' >
          {title}
          <p>{description}</p>
        </div>
        <div className='plan-card-header-price' >{price}</div>
        {/* {id === 'fullBody' ? <span>/once-off</span> : ''} */}
      </div>
    </div>
  );
};

const BloodTestPerYear = ({
  selectedTestCount,
  onTestCountChange,
  availableTestCounts
}: {
  selectedTestCount: number | null;
  onTestCountChange: (count: number) => void;
  availableTestCounts: number[];
}) => {
  return (
    <div className='blood-test-per-year' >
      <div className='text-center' >
        <h4>Select the amount of blood tests you want per year</h4>
        <p><CheckCircleIcon />Includes free nurse blood draw at home</p>
      </div>
      <div className='blood-test-per-year-buttons' >
        {availableTestCounts.map(count => (
          <button 
            key={count}
            onClick={(e) => {
              e.stopPropagation();
              onTestCountChange(count);
            }} 
            className={`btn ${selectedTestCount === count ? "active" : ""}`}
          >
            {count} test{count > 1 ? 's' : ''}
          </button>
        ))}
      </div>
    </div>
  );
};

const plans = [
  {
    id: 'coreVitals',
    className: 'coreVitals-membership',
    title: 'CoreVitals Membership',
    description: 'Ongoing health tracking without imaging - up to 4x blood tests/year',
    price: <>~ £38<span>/month</span></>,
    showBloodTests: true,
    bannerImg: bannerImg1,
    list: [
      '1 blood biomarker test per year',
      'Includes free nurse blood draw at home',
      'Full biomarker panel',
      'Doctor review + consult',
      'Nutritionist guidance',
      'App access to track progress',
    ],
    nextSteps: [
      "Complete the onboarding form",
      "Book a home blood draw with our nurses, or visit one of our partner clinics across the UK",
      "Review your detailed results in the CoreVitals app",
      "Have a 1:1 call with our GP and nutritionist for personalised insights, and recommendations"
    ]
  },
  {
    id: 'coreVitalsPlus',
    className: 'coreVitals-plus-membership',
    title: 'CoreVitals+ Membership',
    description: 'CoreVitals+ Membership – Deeper insight across bloods combined with MRI imaging.',
    price: <>~ £175<span>/month</span></>,
    showBloodTests: true,
    bannerImg: bannerImg2,
    list: [
      'Annual full-body MRI',
      'Pre- and post-scan clinician consults',
      'Combined blood and MRI insights in-app',
      'Ongoing tracking and progress monitoring'
    ],
    nextSteps: [
      "Complete the onboarding form",
      "Book a home blood draw with our nurses, or visit one of our partner clinics across the UK",
      "Book a full body scan at one of our numerous partner scan centers across the UK",
      "Review your detailed results in the CoreVitals app",
      "Have a 1:1 call with our GP and nutritionist to review results, personalised insights, and recommendations"

    ]
  },
  {
    id: 'fullBody',
    className: 'full-body',
    title: 'Full-body MRI',
    description: 'A one-time internal snapshot',
    price: <>£1650</>,
    showBloodTests: false,
    bannerImg: bannerImg3,
    list: [
      'In-app full-body scan report',
      'Doctor review + consult',
      'Pre and post scan clinician consults',
      'Report organised by body part ',
      '60-90 minutes in the scanner',
    ],
    nextSteps: [
      "Complete the onboarding form",
      "Book a home blood draw with our nurses, or visit one of our partner clinics across the UK",
      "Review your detailed results in the CoreVitals app",
      "Have a 1:1 call with our GP and nutritionist for personalised insights, and recommendations"
    ]
  },
];

const OrderSummary = ({ 
  selectedPlanId, 
  paymentType,
  selectedTestCount,
  getPlanPrice
}: { 
  selectedPlanId: string;
  paymentType: PaymentType;
  selectedTestCount: number | null;
  getPlanPrice: (planId: PlanType) => React.ReactNode;
}) => {
  const selectedPlan = plans.find(plan => plan.id === selectedPlanId);
  const isMobile = useMediaQuery('(max-width: 767px)');

  if (!selectedPlan) {
    return null;
  }

  let itemsToShow = selectedPlan.list;
  if (selectedPlan.id === 'coreVitalsPlus') {
    const coreVitalsPlan = plans.find(plan => plan.id === 'coreVitals');
    itemsToShow = [...(coreVitalsPlan?.list || []), ...selectedPlan.list];
  }
  
  // Build display title with number of tests for core plans
  let displayTitle = selectedPlan.title;
  if (selectedTestCount && (selectedPlan.id === 'coreVitals' || selectedPlan.id === 'coreVitalsPlus')) {
    displayTitle = `${selectedPlan.title} ${selectedTestCount} test${selectedTestCount > 1 ? 's' : ''}`;
  }
  
  // Dynamically update the number of tests in the list
  if (selectedTestCount && (selectedPlan.id === 'coreVitals' || selectedPlan.id === 'coreVitalsPlus')) {
    itemsToShow = itemsToShow.map(item => {
      // Replace "1 blood biomarker test per year" with the dynamic number
      if (item.includes('blood biomarker test per year')) {
        return `${selectedTestCount} blood biomarker test${selectedTestCount > 1 ? 's' : ''} per year`;
      }
      return item;
    });
  }

  return (
    <div className='plan-info'>
      <div className='order-summary-banner'>
        <img src={selectedPlan.bannerImg} alt={`${displayTitle} banner`} />
      </div>
      {!isMobile && <div className='order-summary-title'>{displayTitle}</div>}
      {!isMobile && <p className='order-summary-description'>{selectedPlan.description}</p>}
      {/* {selectedPlan.id === 'coreVitalsPlus' && (
        <div className='order-summary-plus'>Everything in CoreVitals, plus</div>
      )} */}
      <ul className='order-summary-list'>
        {itemsToShow.map((item, index) => (
          <li key={`${item}-${index}`}>
            <img src={checkIcon} alt='check' />
            {item}
          </li>
        ))}
      </ul>
      <div className='total-price'>
        <div>Total</div>
        <div>{getPlanPrice(selectedPlanId as PlanType)}</div>
      </div>
    </div>
  );
};

const ConfirmationOrderSummary = ({ 
  selectedPlanId, 
  hideTotalPrice = false,
  selectedTestCount
}: { 
  selectedPlanId: string, 
  hideTotalPrice?: boolean,
  selectedTestCount?: number | null
}) => {
  const selectedPlan = plans.find(plan => plan.id === selectedPlanId);
  const isMobile = useMediaQuery('(max-width: 767px)');

  if (!selectedPlan) {
    return null;
  }
  let itemsToShow = selectedPlan.list;
  if (selectedPlan.id === 'coreVitalsPlus') {
    const coreVitalsPlan = plans.find(plan => plan.id === 'coreVitals');
    itemsToShow = [...(coreVitalsPlan?.list || []), ...selectedPlan.list];
  }
  
  // Build display title with number of tests for core plans
  let displayTitle = selectedPlan.title;
  if (selectedTestCount && (selectedPlan.id === 'coreVitals' || selectedPlan.id === 'coreVitalsPlus')) {
    displayTitle = `${selectedPlan.title} ${selectedTestCount} test${selectedTestCount > 1 ? 's' : ''}`;
  }
  
  // Dynamically update the number of tests in the list
  if (selectedTestCount && (selectedPlan.id === 'coreVitals' || selectedPlan.id === 'coreVitalsPlus')) {
    itemsToShow = itemsToShow.map(item => {
      // Replace "1 blood biomarker test per year" with the dynamic number
      if (item.includes('blood biomarker test per year')) {
        return `${selectedTestCount} blood biomarker test${selectedTestCount > 1 ? 's' : ''} per year`;
      }
      return item;
    });
  }
  
  return (
    <div className='plan-info'>
      <div className='order-summary-banner'>
        <img src={selectedPlan.bannerImg} alt={`${displayTitle} banner`} />
      </div>
      {!isMobile && <div className='order-summary-title'>{displayTitle}</div>}
      {!isMobile && <p className='order-summary-description'>{selectedPlan.description}</p>}
      <ul className='order-summary-list'>
        {itemsToShow.map((item, index) => (
          <li key={`${item}-${index}`}>
            <img src={checkIcon} alt='check' />
            {item}
          </li>
        ))}
      </ul>
    </div>
  );
};

interface PlanOrderSummaryProps {
  selectedPlanId: string;
  showAccordion?: boolean;
  showTitle?: boolean;
  showDescription?: boolean;
  className?: string;
}

const PlanOrderSummary = ({ 
  selectedPlanId, 
  showAccordion = true, 
  showTitle = true, 
  showDescription = true,
  className = '',
  getPlanPrice,
  selectedTestCount
}: PlanOrderSummaryProps & { 
  getPlanPrice: (planId: PlanType) => React.ReactNode;
  selectedTestCount?: number | null;
}) => {
  const [showWhatIncludes, setShowWhatIncludes] = useState<boolean>(false);
  const selectedPlan = plans.find(plan => plan.id === selectedPlanId);
  const isMobile = useMediaQuery('(max-width: 767px)');
  if (!selectedPlan) {
    return null;
  }

  // Get items to show - combine lists for coreVitalsPlus
  let itemsToShow = selectedPlan.list;
  if (selectedPlan.id === 'coreVitalsPlus') {
    const coreVitalsPlan = plans.find(plan => plan.id === 'coreVitals');
    itemsToShow = [...(coreVitalsPlan?.list || []), ...selectedPlan.list];
  }
  
  // Build display title with number of tests for core plans
  let displayTitle = selectedPlan.title;
  if (selectedTestCount && (selectedPlan.id === 'coreVitals' || selectedPlan.id === 'coreVitalsPlus')) {
    displayTitle = `${selectedPlan.title} ${selectedTestCount} test${selectedTestCount > 1 ? 's' : ''}`;
  }
  
  // Dynamically update the number of tests in the list
  if (selectedTestCount && (selectedPlan.id === 'coreVitals' || selectedPlan.id === 'coreVitalsPlus')) {
    itemsToShow = itemsToShow.map(item => {
      // Replace "1 blood biomarker test per year" with the dynamic number
      if (item.includes('blood biomarker test per year')) {
        return `${selectedTestCount} blood biomarker test${selectedTestCount > 1 ? 's' : ''} per year`;
      }
      return item;
    });
  }

  return (
    <div className={`inner-card plan-info ${className} ${showWhatIncludes ? 'active' : ''}`}>
      <h4 onClick={() => setShowWhatIncludes(!showWhatIncludes)} className='inner-card-title d-flex justify-content-between align-items-center'>Order summary {isMobile && <AccordArrow />}</h4>
      {isMobile && !showWhatIncludes && <div className='order-summary-description' >{displayTitle}</div>}
      {selectedPlan && (
        <div>
          <div className='order-summary-content'>
          <img 
            className='order-summary-banner-img' 
            src={selectedPlan.bannerImg} 
            alt={`${displayTitle} banner`} 
          />
          {showTitle && (
            <h3 className='order-summary-title'>{displayTitle}</h3>
          )}
          {showDescription && (
            <p className='order-summary-description'>{selectedPlan.description}</p>
          )}
          
          {showAccordion ? (
            <div 
              onClick={() => setShowWhatIncludes(!showWhatIncludes)} 
              className={`what-includes ${showWhatIncludes ? 'active' : ''} ${selectedPlan.id}`}
            >
              {!isMobile && <div className='what-includes-title'>
                <span>What includes</span>
                <AccordArrow />
              </div>}
              <div className='what-includes-content'>
                <ul className='order-summary-list'>
                  {itemsToShow.map(item => (
                    <li key={item}>
                      <img src={checkIcon} alt='check' />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ) : (
            <ul className='order-summary-list'>
              {itemsToShow.map(item => (
                <li key={item}>
                  <img src={checkIcon} alt='check' />
                  {item}
                </li>
              ))}
            </ul>
          )}
          </div>
          <div className={`total-price ${isMobile ? '' : 'no-border'}`}>
            <div>Total</div>
            <div>{getPlanPrice(selectedPlanId as PlanType)}</div>
          </div>
        </div>
      )}
    </div>
  );
};

type PlanType = 'coreVitals' | 'coreVitalsPlus' | 'fullBody' | '';
type PaymentType = 'monthly' | 'yearly';

interface SelectedPlanData {
  type: PlanType;
  testCount: number | null;
  product: StripeProduct | null;
  productId: string | null;
  paymentType: PaymentType;
}

const Plan = () => {
  const navigate = useNavigate();
  const [pricingStep, setPricingStep] = useState<string>("select plan");
  const [selectedPlan, setSelectedPlan] = useState<PlanType>("");
  const [selectedTestCount, setSelectedTestCount] = useState<number | null>(null);
  const [paymentType, setPaymentType] = useState<PaymentType>('monthly');
  const [products, setProducts] = useState<StripeProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [keepMeUpdated, setKeepMeUpdated] = useState(true);
  const [showCoupon, setShowCoupon] = useState(false);
  const [couponApplied] = useState(false);
  const [showOrderSummary, setShowOrderSummary] = useState(false);
  const isMobile = useMediaQuery('(max-width: 767px)');
  useRhReferralPricing();

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const response = await getStripeProducts();
        const activeProducts = response.products.filter((product: StripeProduct) => product.active);
        setProducts(activeProducts);
      } catch (err: any) {
        console.error('Error fetching products:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  // Get products by type
  const getCoreVitalsProducts = () => {
    return products
      .filter(p => p.key.startsWith('core_') && !p.key.startsWith('core_plus_'))
      .sort((a, b) => {
        const aNum = parseInt(a.key.match(/\d+/)?.[0] || '0');
        const bNum = parseInt(b.key.match(/\d+/)?.[0] || '0');
        return aNum - bNum;
      });
  };

  const getCoreVitalsPlusProducts = () => {
    return products
      .filter(p => p.key.startsWith('core_plus_'))
      .sort((a, b) => {
        const aNum = parseInt(a.key.match(/\d+/)?.[0] || '0');
        const bNum = parseInt(b.key.match(/\d+/)?.[0] || '0');
        return aNum - bNum;
      });
  };

  const getFullBodyMRIProduct = () => {
    return products.find(p => p.key === 'full_body_mri');
  };

  // Get available test counts for selected plan
  const getAvailableTestCounts = (): number[] => {
    if (selectedPlan === 'coreVitals') {
      return getCoreVitalsProducts().map(p => parseInt(p.key.match(/\d+/)?.[0] || '0'));
    } else if (selectedPlan === 'coreVitalsPlus') {
      return getCoreVitalsPlusProducts().map(p => parseInt(p.key.match(/\d+/)?.[0] || '0'));
    }
    return [];
  };

  // Get product for selected plan and test count
  const getSelectedProduct = (): StripeProduct | null => {
    if (!selectedPlan || selectedPlan === 'fullBody') {
      return getFullBodyMRIProduct() || null;
    }
    
    if (!selectedTestCount) return null;
    
    const productKey = selectedPlan === 'coreVitals' 
      ? `core_${selectedTestCount}_test`
      : `core_plus_${selectedTestCount}_test`;
    
    return products.find(p => p.key === productKey) || null;
  };

  // Get price for selected plan
  const getPlanPrice = (planId: PlanType): React.ReactNode => {
    if (planId === 'fullBody') {
      const mriProduct = getFullBodyMRIProduct();
      const amount = mriProduct?.yearly_amount ?? 1650;
      return renderCheckoutPlanPrice({
        planId: 'fullBody',
        amount,
        paymentType,
        onceOff: true,
      });
    }

    // Get the product to use for pricing
    let productToUse: StripeProduct | null = null;
    
    if (selectedPlan === planId && selectedTestCount) {
      // Use selected product if plan and test count are selected
      productToUse = getSelectedProduct();
    }
    
    // If no product found, use default (first available) for this plan type
    if (!productToUse) {
      const availableProducts = planId === 'coreVitals' 
        ? getCoreVitalsProducts()
        : getCoreVitalsPlusProducts();
      productToUse = availableProducts[0] || null;
    }

    if (productToUse) {
      const amount = paymentType === 'monthly' 
        ? productToUse.monthly_amount 
        : productToUse.yearly_amount;
      
      if (amount !== null && amount !== undefined) {
        const showTilde = !(selectedPlan === planId && selectedTestCount);
        return renderCheckoutPlanPrice({
          planId,
          amount,
          paymentType,
          showTilde,
        });
      }
    }
    
    const fallbackAmount =
      getPlanFallbackAmount(planId) ?? (planId === 'coreVitals' ? 38 : 175);
    return renderCheckoutPlanPrice({
      planId,
      amount: fallbackAmount,
      paymentType,
      showTilde: true,
    });
  };

  const handlePlanSelect = (planId: PlanType) => {
    setSelectedPlan(planId);
    if (planId === 'fullBody') {
      setSelectedTestCount(null);
      // Auto-select yearly for fullBody
      const newPaymentType = 'yearly';
      setPaymentType(newPaymentType);
      const product = getFullBodyMRIProduct();
      firePlanChange(planId, null, newPaymentType, product);
      firePaymentTypeChange(newPaymentType, planId, null);
    } else {
      // Auto-select first test count
      const availableCounts = getAvailableTestCounts();
      if (availableCounts.length > 0) {
        const firstCount = availableCounts[0];
        setSelectedTestCount(firstCount);
        // Get product for the selected plan and test count
        const productKey = planId === 'coreVitals' 
          ? `core_${firstCount}_test`
          : `core_plus_${firstCount}_test`;
        const product = products.find(p => p.key === productKey) || null;
        firePlanChange(planId, firstCount, paymentType, product);
      } else {
        const product = getSelectedProduct();
        firePlanChange(planId, selectedTestCount, paymentType, product);
      }
    }
  };

  const handleTestCountSelect = (count: number) => {
    setSelectedTestCount(count);
    const product = getSelectedProduct();
    firePlanChange(selectedPlan, count, paymentType, product);
  };

  const handleNextToDetails = () => {
    if (!selectedPlan) return;
    
    const product = getSelectedProduct();
    let productId: string | null = null;
    
    if (product) {
      productId = paymentType === 'monthly' 
        ? product.stripe_price_monthly_id 
        : product.stripe_price_yearly_id;
    } else if (selectedPlan === 'fullBody') {
      const mriProduct = getFullBodyMRIProduct();
      if (mriProduct) {
        productId = paymentType === 'monthly' 
          ? mriProduct.stripe_price_monthly_id 
          : mriProduct.stripe_price_yearly_id;
      }
    }

    // Store selected plan data
    const planData: SelectedPlanData = {
      type: selectedPlan,
      testCount: selectedTestCount,
      product: product,
      productId: productId,
      paymentType: paymentType,
    };
    localStorage.setItem('selectedPlan', JSON.stringify(planData));

    if (selectedPlan === 'fullBody') {
      // Starting a fresh scan checkout should always begin at the scan-plan selection
      // screen (not restore an old checkout step like step3 from a previous attempt).
      try {
        sessionStorage.setItem('scanCheckoutStep', 'landing');
        sessionStorage.removeItem('scanType');
        sessionStorage.removeItem('scanCheckoutLastPostcode');
        sessionStorage.removeItem('scanCheckoutClinicsCache');
      } catch {}

      try {
        localStorage.removeItem('bookingData');
        localStorage.removeItem('signupResponse');
        localStorage.removeItem('appliedPromocode');
        localStorage.removeItem('subscriptionAmount');
        // Will be overwritten again when user selects scan type inside scan checkout.
        localStorage.removeItem('selectedPlan');
      } catch {}

      navigate('/full-body-scan');
    } else {
      navigate('/signup/today');
    }
  };
  return (
    <div className='c_container subscription-page-wrapper'>
      {pricingStep === "select plan" && <div className='two-colum-grid' >
        <div className='left-column' >
          <div className='journey-starts-here' >Your journey starts here</div>
          <h2 className='select-plan-title' >Select a plan below</h2>
          <div className='choose-between-monthly-yearly' >Choose between monthly or yearly payment 
            <span className='tooltip-icon' >
              ⓘ
              <span className='tooltip-text' >
                <span className='tooltip-text-content' >You get all services up front. You can pay in 1 annual payment or in 12 monthly payments. Monthly payments commit you to the full 12-month term.</span>
              </span>
            </span>
          </div>
          <div className='d-flex ' >
            <div className='plan-term-check' >
              <label className={selectedPlan === 'fullBody' ? 'disabled' : ''}>
                <input 
                  type='radio' 
                  name='plan-term' 
                  checked={paymentType === 'monthly'}
                  onChange={() => {
                    setPaymentType('monthly');
                    firePaymentTypeChange('monthly', selectedPlan, selectedTestCount);
                  }}
                  disabled={selectedPlan === 'fullBody'}
                />
                <span className='btn' >Monthly</span>
              </label>
              <label>
                <input 
                  type='radio' 
                  name='plan-term' 
                  checked={paymentType === 'yearly'}
                  onChange={() => {
                    setPaymentType('yearly');
                    firePaymentTypeChange('yearly', selectedPlan, selectedTestCount);
                  }}
                />
                <span className='btn' >Yearly</span>
              </label>
            </div>
          </div>
          <div className='' >
            {loading && products.length === 0 && (
              <div className='plans-loading-hint'>Loading latest prices…</div>
            )}
            <>
                {/* CoreVitals Membership */}
                <div
                  className={`plan-card-wrapper coreVitals-membership ${selectedPlan === 'coreVitals' ? 'active' : ''}`}
                  onClick={() => handlePlanSelect('coreVitals')}
                >
                  <PlanHeader
                    id='coreVitals'
                    title='CoreVitals Membership'
                    description='Ongoing health tracking without imaging - up to 4x blood tests/year'
                    price={getPlanPrice('coreVitals')}
                    selectedPlan={selectedPlan}
                    onSelect={(id) => handlePlanSelect(id as PlanType)}
                  />
                  {selectedPlan === 'coreVitals' && (
                    <BloodTestPerYear
                      selectedTestCount={selectedTestCount}
                      onTestCountChange={handleTestCountSelect}
                      availableTestCounts={getAvailableTestCounts()}
                    />
                  )}
                  {isMobile && selectedPlan === 'coreVitals' && (
                    <OrderSummary 
                      selectedPlanId={selectedPlan} 
                      paymentType={paymentType}
                      selectedTestCount={selectedTestCount}
                      getPlanPrice={getPlanPrice}
                    />
                  )}
                </div>

                {/* CoreVitals+ Membership */}
                <div
                  className={`plan-card-wrapper coreVitals-plus-membership ${selectedPlan === 'coreVitalsPlus' ? 'active' : ''}`}
                  onClick={() => handlePlanSelect('coreVitalsPlus')}
                >
                  <PlanHeader
                    id='coreVitalsPlus'
                    title='CoreVitals+ Membership'
                    description='CoreVitals+ Membership – Deeper insight across bloods combined with MRI imaging.'
                    price={getPlanPrice('coreVitalsPlus')}
                    selectedPlan={selectedPlan}
                    onSelect={(id) => handlePlanSelect(id as PlanType)}
                  />
                  {selectedPlan === 'coreVitalsPlus' && (
                    <BloodTestPerYear
                      selectedTestCount={selectedTestCount}
                      onTestCountChange={handleTestCountSelect}
                      availableTestCounts={getAvailableTestCounts()}
                    />
                  )}
                  {isMobile && selectedPlan === 'coreVitalsPlus' && (
                    <OrderSummary 
                      selectedPlanId={selectedPlan} 
                      paymentType={paymentType}
                      selectedTestCount={selectedTestCount}
                      getPlanPrice={getPlanPrice}
                    />
                  )}
                </div>

                {/* Full-body MRI */}
                <div
                  className={`plan-card-wrapper full-body ${selectedPlan === 'fullBody' ? 'active' : ''}`}
                  onClick={() => handlePlanSelect('fullBody')}
                >
                  <PlanHeader
                    id='fullBody'
                    title='Full-body MRI'
                    description='A one-time internal snapshot'
                    price={getPlanPrice('fullBody')}
                    selectedPlan={selectedPlan}
                    onSelect={(id) => handlePlanSelect(id as PlanType)}
                  />
                  {isMobile && selectedPlan === 'fullBody' && (
                    <OrderSummary 
                      selectedPlanId={selectedPlan} 
                      paymentType={paymentType}
                      selectedTestCount={null}
                      getPlanPrice={getPlanPrice}
                    />
                  )}
                </div>
            </>

            <div>
              <button 
                disabled={!selectedPlan || (selectedPlan !== 'fullBody' && !selectedTestCount)} 
                onClick={handleNextToDetails} 
                className='plans-step-btn'
              >
                {selectedPlan === 'fullBody' ? 'Scan Plan' : 'Next - your details'} <img src={arrowRightIcon} alt='arrow right' />
              </button>
            </div>
          </div>
        </div>
        {!isMobile && (
          <div className='right-column'>
            <div className='inner-card'>
            <h4 className='inner-card-title'>Order summary</h4>
            {selectedPlan ? (
              <OrderSummary 
                selectedPlanId={selectedPlan} 
                paymentType={paymentType}
                selectedTestCount={selectedTestCount}
                getPlanPrice={getPlanPrice}
              />
            ) : (
              <div className='inner-card-info'>
                <div className='select-plan-text'>Select a plan to get started.</div>
              </div>
            )}
            </div>
          </div>
        )}
      </div>}
      {pricingStep === "your details" && <div className='two-colum-grid' >
        <div className='left-column' >
          <h2 className='select-plan-title' >Enter your details</h2>
          <form className='your-details-form' >
              <div className='input-label'>
                <label className='labelStyle'>
                  Full name<span className='required'>*</span>
                </label>
                <TextInput 
                  type='text' 
                  name='fullName' 
                  placeholder='Enter name' 
                  value={""}
                  // onChange={handleInputChange}
                />
                {/* {errors.fullName && <div className='error-message'>{errors.fullName}</div>} */}
              </div>
              
              <div className='input-label top-space'>
                <label className='labelStyle'>
                  Email address<span className='required'>*</span>
                </label>
                <TextInput 
                  type='email' 
                  name='email' 
                  placeholder='Enter email' 
                  value={""}
                  // onChange={handleInputChange}
                />
              </div>
              
              <div className='input-label top-space'>
                <label className='labelStyle'>
                  Phone number<span className='required'>*</span>
                </label>
                <PhoneInputComp
                  placeholder='Enter phone number'
                  selectedPhone={"formData.phone"}
                  onPhoneChange={() => {}}
                />
              </div>
            </form>
          {isMobile && <div>
            <PlanOrderSummary 
              selectedPlanId={selectedPlan} 
              showAccordion={true}
              showTitle={true}
              showDescription={true}
              getPlanPrice={getPlanPrice}
              selectedTestCount={selectedTestCount}
            />
          </div>}
          <div className='input-label keep-me-updated'>
            <label className='d-flex align-items-center gap-20'>
              <input type='checkbox' checked={keepMeUpdated} onChange={() => setKeepMeUpdated(!keepMeUpdated)} />
              <span className='checkbox-style' ></span>
              <span>Keep me updated on member-only events and offers.</span>
            </label>
          </div>
          
          {!keepMeUpdated && <div className='privacy-policy-text'>
          We collect your responses to understand your interests, tailor relevant content to you, and let you know when a spot becomes available to join CoreVitals. Your data is stored securely and handled in accordance with our privacy policy. <br/>
          
          We will never share your personal information with third parties without your consent. For more information, please review our privacy policy at: <a href="https://www.corevitals.me/privacy-policy">https://www.corevitals.me/privacy-policy</a>
          </div>}
          <button onClick={() => setShowCoupon(!showCoupon)} className='discount-code-btn'>
            Have a discount code?
            <img src={arrow} alt="Arrow" />
          </button>
          {showCoupon && <div className='discount-code-section'>
            <div className='discount-code-container'>
              <label className='discount-code-label'>Discount code</label>
              <div className='discount-input-group'>
                <TextInput 
                  type='text' 
                  name='promocode' 
                  placeholder='Enter discount code here' 
                  // value={formData.promocode}
                  // onChange={handleInputChange}
                  // isDisabled={couponApplied}
                />
                <button
                  type='button'
                  className='apply-discount-btn'
                  // onClick={couponApplied ? handleRemoveCoupon : handleApplyCoupon}
                >
                  <span>{couponApplied ? 'REMOVE' : 'APPLY'}</span>&nbsp;
                  <span>DISCOUNT</span>
                </button>
              </div>
            </div>
          </div>}
          <div>
              <button onClick={() => setPricingStep("payment")} className='plans-step-btn' >Continue <img src={arrowRightIcon} alt='arrow right' /></button>
          </div>
        </div>
        {!isMobile && <div className='right-column' >
          <PlanOrderSummary 
            selectedPlanId={selectedPlan} 
            showAccordion={true}
            showTitle={true}
            showDescription={true}
            getPlanPrice={getPlanPrice}
            selectedTestCount={selectedTestCount}
          />
        </div>}
      </div>}

      {pricingStep === "payment" && <div className='two-colum-grid' >
        <div className='left-column' >
          <h2 className='select-plan-title' >Secure your order</h2>
          {isMobile && <div>
            <PlanOrderSummary 
              selectedPlanId={selectedPlan} 
              showAccordion={true}
              showTitle={true}
              showDescription={true}
              getPlanPrice={getPlanPrice}
              selectedTestCount={selectedTestCount}
            />
          </div>}
          <div className='' >
            <div className='payment-form' >Add Payment form here</div>
          <button onClick={() => setPricingStep("confirmation")} className='plans-step-btn' >
            This is temporary button to go to confirmation page
          </button>
          </div>
        </div>
        {!isMobile && <div className='right-column' >
          <PlanOrderSummary 
            selectedPlanId={selectedPlan} 
            showAccordion={true}
            showTitle={true}
            showDescription={true}
            getPlanPrice={getPlanPrice}
            selectedTestCount={selectedTestCount}
          />
        </div>}
      </div>}
      
      {pricingStep === "confirmation" && (
        <div className='two-colum-grid'>
          <div className='left-column'>
            <div className='journey-starts-here'>Welcome to CoreVitals</div>
            <h2 className='select-plan-title'>Thanks for joining us, *user first name*!</h2>
            {isMobile &&  <div className={`inner-card order-summary-card ${showOrderSummary ? 'active' : ''}`}>
              <h4 className='inner-card-title' onClick={() => setShowOrderSummary(!showOrderSummary)}>Order summary <AccordArrow /></h4>
              {!showOrderSummary && <div className='order-summary-description' >{(() => {
                const plan = plans.find(plan => plan.id === selectedPlan);
                if (!plan) return 'Order summary';
                let title = plan.title;
                if (selectedTestCount && (plan.id === 'coreVitals' || plan.id === 'coreVitalsPlus')) {
                  title = `${plan.title} ${selectedTestCount} test${selectedTestCount > 1 ? 's' : ''}`;
                }
                return title;
              })()}</div>}
              {showOrderSummary && <ConfirmationOrderSummary selectedPlanId={selectedPlan} selectedTestCount={selectedTestCount} />}
              </div>}
            <div className={`confirmation-steps ${plans.find(plan => plan.id === selectedPlan)?.id}`}>
              <h4 className='confirmation-steps-title'>Next steps:</h4>
              {plans.find(plan => plan.id === selectedPlan)?.nextSteps?.map((step, index) => (
                <div key={`${step}-${index}`}>
                  <h5>Step {index + 1}</h5>
                  <p>{step}</p>
                </div>
              ))}
            </div>
            <div className='help-section-pricing'>
              <h3>Need help?</h3>
              <p>
                If you have any questions, just reply to your confirmation email
                or reach out at&nbsp;
                <a href='mailto:members@corevitals.me'>members@corevitals.me</a>
              </p>
            </div>
            <div>
              <button
                onClick={() => setPricingStep("confirmation")}
                className='plans-step-btn top40'
              >
                Continue to onboarding form <img src={arrowRightIcon} alt='arrow right' />
              </button>
            </div>
          </div>
          {!isMobile && (
            <div className='right-column'>
              <div className='inner-card'>
                <ConfirmationOrderSummary selectedPlanId={selectedPlan} selectedTestCount={selectedTestCount} />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Plan;